import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getPolicyDetails } from "service/policy/getPolicyDetails";
import moment from "moment";
import config from "config/config";
import { getTypeOfPolicy, isFlagEnabled } from "js/utilities";
import { getToken } from "js/formatting/getToken";

export const searchPolicies = createAsyncThunk(
  "searchPolicies",
  async (taxId, { getState, rejectWithValue, fulfillWithValue }) => {
    try {
      const options = createFetchOptions(taxId);
      const response = await fetch(
        config.ODS_API_BASE_URL + "/ce-callerVerification/SearchPolicy",
        options
      );

      if (!response.ok) {
        return handleErrorResponse(rejectWithValue, [], response.status);
      }

      const data = await response.json();

      if (data?.gXdpApi?.success) {
        const {
          allPolicies,
          allClaims,
          isRestrictedPolicy,
          isDomesticViolencePolicy,
          isOwnerDeathClaimExists,
        } = await processPolicyData(data, getState);
        return fulfillWithValue({
          policies: allPolicies,
          claims: allClaims,
          status: response.status,
          hasRestrictedPolicy: isRestrictedPolicy,
          hasDomesticViolencePolicy: isDomesticViolencePolicy,
          hasOwnerDeathClaimExists: isOwnerDeathClaimExists,
        });
      } else {
        return handleErrorResponse(
          rejectWithValue,
          [],
          null,
          data?.gXdpApi?.gIdpPayLoad?.addOnProperties?.lifesys?.policyHolder
            ?.errorMessage
        );
      }
    } catch (error) {
      return handleErrorResponse(rejectWithValue, [], "Something went wrong.");
    }
  }
);

// Create a function to handle generating fetch options
const createFetchOptions = (taxId) => ({
  method: "POST",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Ocp-Apim-Subscription-Key": config.ODS_API_KEY,
    Authorization: `bearer ${getToken()}`,
  },
  body: JSON.stringify({
    gXdpApi: {
      transactionType: {
        sourceNameSpace: "ODS",
        targetNameSpace: "GIDP",
        eventName: "SEARCHPOLICY",
        operation: "GET",
      },
      gIdpPayLoad: {
        policyHolder: { taxId: taxId },
        addOnProperties: {
          company: config.COMPANY,
          clientName: config.CLIENTNAME,
          system: "LSN",
          lifeSysDataset: "P",
          searchType: "OW,JO",
          recordsRequested: 100,
        },
      },
      parentCorrelationID: moment().format("YYYY-MM-DD HH:MM:SS"),
      correlationID: moment().format("YYYY-MM-DD HH:MM:SS"),
    },
  }),
});

// Create a function to handle the error response
const handleErrorResponse = (
  rejectWithValue,
  allPolicies,
  status,
  errorMessage
) => {
  return rejectWithValue({
    policies: allPolicies,
    claims: [],
    status: status || errorMessage || "Unknown error",
  });
};

// Function to process the policy data
const processPolicyData = async (data, getState) => {
  const allPolicies = [];
  const allClaims = [];
  let isRestrictedPolicy = false;
  let isDomesticViolencePolicy = false;
  let isOwnerDeathClaimExists = false;
  const VALID_RESTRICTED_POLICY_TYPE =
    config.VALID_RESTRICTED_POLICY_TYPE.split(",");

  // Extract contract IDs to fetch details
  const policyIDs = data.gXdpApi.gIdpPayLoad.policyHolder.policies.filter(
    (p) => p.contractID?.length <= 8
  );

  for (const policy of policyIDs) {
    const response = await getPolicyDetails(
      policy.contractID,
      getState().user?.user?.userId
    );

    if (isPolicyExcluded(response, VALID_RESTRICTED_POLICY_TYPE)) {
      isRestrictedPolicy = true;
    }
    if (hasDomesticViolenceFlag(response)) {
      isDomesticViolencePolicy = true;
    }
    if (isCFPolicy(response)) {
      isRestrictedPolicy = true;
    }

    if (response.ownerDeathClaimExists) {
      isOwnerDeathClaimExists = true;
    }

    // Adding policy and claim data
    if (
      !isPolicyExcluded(response, VALID_RESTRICTED_POLICY_TYPE) &&
      !hasDomesticViolenceFlag(response) &&
      response.ownerTypeMatch &&
      !response.ownerDeathClaimExists &&
      !isCFPolicy(response)
    )
      handlePolicyAndClaims(allPolicies, allClaims, response);
  }

  return {
    allPolicies,
    allClaims,
    isRestrictedPolicy,
    isDomesticViolencePolicy,
    isOwnerDeathClaimExists,
  };
};

// Function to check if the policy is excluded based on its type
const isPolicyExcluded = (response, VALID_RESTRICTED_POLICY_TYPE) => {
  return (
    (getTypeOfPolicy(response.product.type) === "annuity" &&
      !isFlagEnabled("ENABLE_ANNUITIES")) ||
    (response?.product?.type &&
      VALID_RESTRICTED_POLICY_TYPE.includes(
        response?.product?.type?.toLowerCase()
      ))
  );
};

// Function to check if the policy has a domestic violence flag
const hasDomesticViolenceFlag = (response) => {
  return response?.domesticViolenceIndicator === "Y";
};

// Function to check if the policy is CF policy
const isCFPolicy = (response) => {
  return (
    response.sourceSystemName == "CALCFOCUS" &&
    config.ENABLE_RESTRICT_CF_POLICY == "true"
  );
};

// Function to handle adding policy and claims to their respective arrays
const handlePolicyAndClaims = (allPolicies, allClaims, response) => {
  const activeStatusList = [
    "lapsed",
    "terminated",
    "cancelled",
    "surrendered",
    "matured",
    "expired",
    "administrative rewrite",
  ];
  const isActivePolicy =
    activeStatusList.includes(response?.status?.toLowerCase()) &&
    response.statusDate &&
    moment().diff(moment(response.statusDate), "years", true) <= 2;

  if (isActivePolicy) allPolicies.push(response);
  if (response?.deathClaim && isRelevantDeathClaim(response.deathClaim)) {
    allClaims.push({
      ...response.deathClaim,
      productType: response.product.type,
      contractID: response.contractID,
      oldPolicy: response.oldPolicy,
      claimNumber: response.deathClaim.deathClaimNumber,
      claimStatus: response.deathClaim.deathClaimStatus,
    });
  }

  if (
    config.ENABLE_DISABILITYCLAIMS === "true" &&
    response?.disabilityClaim?.length > 0
  ) {
    response.disabilityClaim.map((disabilityClaim) => {
      allClaims.push({
        ...disabilityClaim,
        productType: response.product.type,
        dateOfNotification: disabilityClaim.claimOpenedDate,
        contractID: response.contractID,
        oldPolicy: response.oldPolicy,
      });
    });
  }

  // Also push non-active policies for further analysis or storage if needed
  if (!isActivePolicy) {
    allPolicies.push(response);
  }
};

const isRelevantDeathClaim = (deathClaim) => {
  const relevantStatuses = ["01", "1", "92", "93", "97", "98", "99"];
  return relevantStatuses.includes(deathClaim?.deathClaimStatus);
};

// Remove policies action remains unchanged
export const removePolicies = createAsyncThunk(
  "removePolicies",
  async (policies = {}, { rejectWithValue, fulfillWithValue }) => {
    return fulfillWithValue(policies);
  }
);

const searchPoliciesSlice = createSlice({
  name: "policies",
  initialState: {
    hasRestrictedPolicy: false,
    hasDomesticViolencePolicy: false,
    hasOwnerDeathClaimExists: false,
    policies: [],
    claims: [],
    isLoading: false,
    error: "",
    status: null,
  },
  extraReducers: {
    [searchPolicies.pending]: (state) => {
      state.isLoading = true;
    },
    [searchPolicies.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.hasRestrictedPolicy = action.payload.hasRestrictedPolicy;
      state.hasOwnerDeathClaimExists = action.payload.hasOwnerDeathClaimExists;
      state.hasDomesticViolencePolicy =
        action.payload.hasDomesticViolencePolicy;
      state.policies = action.payload.policies;
      state.claims = action.payload.claims;
      state.status = action.payload.status;
    },
    [searchPolicies.rejected]: (state, action) => {
      state.isLoading = false;
      state.error = action.error; // Improved error handling
      state.status = action.payload?.status || "Failed due to unknown reason";
      state.policies = action.payload?.policies || [];
      state.claims = action.payload?.claims || [];
    },
    [removePolicies.fulfilled]: (state) => {
      state.policies = [];
      state.claims = [];
      state.isLoading = false;
      state.error = "";
      state.status = null;
    },
  },
});

export default searchPoliciesSlice.reducer;
